/**
 * Sensus — Favoris (wishlist) sans application.
 *
 * - Les favoris sont enregistrés dans le navigateur du visiteur (localStorage) :
 *   liste de « handles » produit. Aucun compte nécessaire, rien côté serveur.
 * - Cœurs : tout bouton [data-sensus-wishlist-toggle][data-product-handle].
 * - Tiroir : <dialog data-sensus-wishlist-dialog>, ouvert par [data-sensus-wishlist-open].
 * - Compteur : [data-sensus-wishlist-count] (bulle du header).
 * - Les infos produit (photo, prix…) sont lues à l'ouverture via /products/<handle>.js.
 */

const STORAGE_KEY = 'sensus-wishlist-v1';
const MAX_ITEMS = 50;
const CLOSE_DURATION = 260;

/** @type {Map<string, any>} */
const productCache = new Map();

/** @returns {string[]} */
function read() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function write(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_ITEMS)));
  } catch {
    /* stockage indisponible (navigation privée stricte) : on ignore */
  }
  updateUI();
  document.dispatchEvent(new CustomEvent('sensus:wishlist:change', { detail: { handles: read() } }));
}

/** @param {string} handle */
function has(handle) {
  return read().includes(handle);
}

/** @param {string} handle */
function toggle(handle) {
  const handles = read();
  const index = handles.indexOf(handle);
  if (index === -1) handles.unshift(handle);
  else handles.splice(index, 1);
  write(handles);
  return index === -1;
}

/** @param {string} handle */
function remove(handle) {
  write(read().filter((h) => h !== handle));
}

/* ------------------------------------------------------------------ UI --- */

function updateUI() {
  const handles = read();

  document.querySelectorAll('[data-sensus-wishlist-toggle]').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;
    const handle = button.dataset.productHandle || '';
    const active = handles.includes(handle);
    const title = button.dataset.productTitle || '';
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? `Retirer ${title} des favoris` : `Ajouter ${title} aux favoris`);
    const label = button.querySelector('.sensus-wishlist-toggle__label');
    if (label instanceof HTMLElement) {
      const text = (active ? label.dataset.labelRemove : label.dataset.labelAdd) || label.textContent || '';
      if (label.textContent?.trim() !== text) label.textContent = text;
    }
  });

  document.querySelectorAll('[data-sensus-wishlist-count]').forEach((bubble) => {
    if (!(bubble instanceof HTMLElement)) return;
    const count = String(handles.length);
    if (bubble.textContent !== count) bubble.textContent = count;
    bubble.hidden = handles.length === 0;
  });

  document.querySelectorAll('[data-sensus-wishlist-count-text]').forEach((el) => {
    const text = handles.length ? `(${handles.length})` : '';
    if (el.textContent !== text) el.textContent = text;
  });
}

/* -------------------------------------------------------------- Tiroir --- */

function dialog() {
  const el = document.querySelector('[data-sensus-wishlist-dialog]');
  return el instanceof HTMLDialogElement ? el : null;
}

/** @param {number} cents */
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'EUR';
  const locale = document.documentElement.lang || 'fr';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} €`;
  }
}

/** @param {string} src @param {number} width */
function sizedImage(src, width) {
  if (!src) return '';
  try {
    const url = new URL(src, location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

/** @param {string} handle */
async function fetchProduct(handle) {
  if (productCache.has(handle)) return productCache.get(handle);
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(String(response.status));
  const product = await response.json();
  productCache.set(handle, product);
  return product;
}

/** Échappe un texte pour l'insérer sans risque dans du HTML, y compris dans un attribut.
 * @param {unknown} text */
function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, (char) => ESCAPES[char] || char);
}

/** @type {Record<string, string>} */
const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Exécute `task` sur chaque élément, avec au plus `limit` requêtes en même temps.
 * @template T, R
 * @param {T[]} items @param {number} limit @param {(item: T) => Promise<R>} task
 * @returns {Promise<PromiseSettledResult<R>[]>} */
async function settleWithLimit(items, limit, task) {
  /** @type {PromiseSettledResult<R>[]} */
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      try {
        results[index] = { status: 'fulfilled', value: await task(items[index]) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/** @param {any} product */
function itemMarkup(product) {
  const image = sizedImage(product.featured_image, 300);
  const price = product.price_varies ? `Dès ${formatMoney(product.price_min)}` : formatMoney(product.price);
  const multipleVariants = product.variants && product.variants.length > 1;
  const cta = !product.available ? 'Voir le produit' : multipleVariants ? 'Choisir ma taille' : 'Voir le produit';
  const title = escapeHtml(product.title);
  const url = escapeHtml(product.url);
  const src = escapeHtml(image);

  return `
    <li class="sensus-wishlist__item" data-handle="${escapeHtml(product.handle)}">
      <a class="sensus-wishlist__media" href="${url}" tabindex="-1" aria-hidden="true">
        ${image ? `<img src="${src}" alt="" width="300" height="300" loading="lazy">` : ''}
      </a>
      <div class="sensus-wishlist__info">
        <a class="sensus-wishlist__name" href="${url}">${title}</a>
        <p class="sensus-wishlist__price">${price}</p>
        ${product.available ? '' : '<p class="sensus-wishlist__status">Indisponible</p>'}
        <a class="sensus-wishlist__cta" href="${url}">${cta}</a>
      </div>
      <button type="button" class="sensus-wishlist__remove" data-sensus-wishlist-remove="${escapeHtml(product.handle)}" aria-label="Retirer ${title} des favoris">
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
    </li>`;
}

async function renderList() {
  const el = dialog();
  if (!el) return;
  const list = el.querySelector('[data-sensus-wishlist-list]');
  const empty = el.querySelector('[data-sensus-wishlist-empty]');
  if (!(list instanceof HTMLElement) || !(empty instanceof HTMLElement)) return;

  const handles = read();
  empty.hidden = handles.length > 0;

  if (!handles.length) {
    list.innerHTML = '';
    return;
  }

  list.setAttribute('aria-busy', 'true');
  if (!list.children.length) {
    list.innerHTML = handles.map(() => '<li class="sensus-wishlist__item sensus-wishlist__item--loading" aria-hidden="true"></li>').join('');
  }

  const results = await settleWithLimit(handles, 6, fetchProduct);
  const products = [];
  const missing = [];
  results.forEach((result, i) => {
    if (result.status === 'fulfilled' && result.value) products.push(result.value);
    else if (result.status === 'fulfilled' && !result.value) missing.push(handles[i]);
  });

  // Produits supprimés de la boutique : on les retire discrètement
  if (missing.length) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(read().filter((h) => !missing.includes(h))));
    } catch {
      /* ignore */
    }
    updateUI();
  }

  list.innerHTML = products.map(itemMarkup).join('');
  list.removeAttribute('aria-busy');
  empty.hidden = products.length > 0;
}

function openDrawer() {
  const el = dialog();
  if (!el || el.open) return;
  el.classList.remove('is-closing');
  el.showModal();
  document.documentElement.setAttribute('scroll-lock', '');
  renderList();
}

function closeDrawer() {
  const el = dialog();
  if (!el || !el.open) return;
  el.classList.add('is-closing');
  window.setTimeout(() => {
    el.close();
    el.classList.remove('is-closing');
  }, CLOSE_DURATION);
}

/* ------------------------------------------------------------ Événements --- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const toggleButton = target.closest('[data-sensus-wishlist-toggle]');
  if (toggleButton instanceof HTMLElement) {
    event.preventDefault();
    event.stopPropagation();
    const added = toggle(toggleButton.dataset.productHandle || '');
    toggleButton.classList.remove('is-bumping');
    void toggleButton.offsetWidth;
    if (added) toggleButton.classList.add('is-bumping');
    return;
  }

  if (target.closest('[data-sensus-wishlist-open]')) {
    event.preventDefault();
    openDrawer();
    return;
  }

  const removeButton = target.closest('[data-sensus-wishlist-remove]');
  if (removeButton instanceof HTMLElement) {
    const item = removeButton.closest('.sensus-wishlist__item');
    item?.classList.add('is-removing');
    window.setTimeout(() => {
      remove(removeButton.dataset.sensusWishlistRemove || '');
      item?.remove();
      const el = dialog();
      const empty = el?.querySelector('[data-sensus-wishlist-empty]');
      if (empty instanceof HTMLElement) empty.hidden = read().length > 0;
    }, 200);
    return;
  }

  if (target.closest('[data-sensus-wishlist-close]')) {
    closeDrawer();
    return;
  }

  // Clic sur le fond (en dehors du panneau)
  const el = dialog();
  if (el && el.open && target === el) closeDrawer();
}, true);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const el = dialog();
  if (el?.open) {
    event.preventDefault();
    closeDrawer();
  }
});

// Fermeture native (ex. touche Échap gérée par le navigateur) : on libère le défilement
document.addEventListener('close', (event) => {
  if (event.target === dialog()) document.documentElement.removeAttribute('scroll-lock');
}, true);

// Synchronisation entre onglets
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) updateUI();
});

// Contenu rechargé (éditeur de thème, filtres de collection, carrousels…) : mise à jour groupée
let scheduled = false;
new MutationObserver((mutations) => {
  if (scheduled) return;
  const relevant = mutations.some((m) =>
    [...m.addedNodes].some((n) => n instanceof Element && (n.matches('[data-sensus-wishlist-toggle]') || n.querySelector('[data-sensus-wishlist-toggle]')))
  );
  if (!relevant) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    updateUI();
  });
}).observe(document.body, { childList: true, subtree: true });

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', updateUI);
else updateUI();
